'use strict';

const { noop } = require('../../mixins/commonjs/basicUtils');
const { getTouchDistance, touchToCoordinates } = require('./utils');
const MultiTouchDetector = require('./MultiTouchDetector');
const { createEnum } = require('../../utils/enum');

/**
 * Grab relevant state data from the passed in event.
 * @param  {Object} e Event
 * @return {Object}   State data
 */
function stateFromTouch(e) {
    const { changedTouches, target } = e;

    return {
        time: Date.now(),
        position: touchToCoordinates(target, changedTouches[0]),
    };
}

// Maximum pixel distance of second tap from first tap.
const THRESHOLD_DISTANCE = 30;
// Maximum time in ms of second tap from first tap.
const THRESHOLD_TIME = 500;
// Maximum time in ms that a touch will count as a tap.
const THRESHOLD_HOLD_TIME = 100;

// State machine for keeping track of how many valid taps have occurred.
const stateEnum = createEnum({
    // Initial state before any taps
    ready: () => {},
    // One successful tap
    first: data => data,
    // Two successful taps
    second: data => data,
});

/**
 * Detect two quick, sequential taps on the given DOM element.
 *
 * @param {Object} options.el The DOM element.
 * @param {Function} [options.onDoubleTap] Callback to fire after a successful double tap.
 */
class DoubleTapDetector {
    constructor({ el, onDoubleTap = noop } = {}) {
        if (!el) {
            throw new Error('Must pass `el` to DoubleTapDetector');
        }

        Object.assign(this, {
            _el: el,
            _onDoubleTap: onDoubleTap,
            _multiTouchDetector: new MultiTouchDetector({
                onSingleTouchStart: this._onSingleTouchStart.bind(this),
                onSingleTouchEnd: this._onSingleTouchEnd.bind(this),
                el,
            }),
        });

        // Set the initial state
        this._resetState();
    }

    _onSingleTouchStart(e) {
        const data = stateFromTouch(e);

        // transition to the next state after a tap
        this._state.match({
            ready: () => {
                this._state = stateEnum.first(data);
                return this._state;
            },
            first: () => {
                this._state = stateEnum.second(data);
                return this._state;
            },
            // No need to handle "second" state here
        });
    }

    _onSingleTouchEnd(e) {
        const endData = stateFromTouch(e);

        // Attempt to transition to the next state if the tap passes validation
        this._state.match({
            // No need to handle "ready" state here
            first: data =>
                this._tryNextState(endData, data, newData => this._handleFirstTap(newData)),
            second: data =>
                this._tryNextState(endData, data, newData => this._handleSecondTap(newData)),
        });
    }

    _resetState() {
        if (this._timer) {
            clearTimeout(this._timer);
        }

        this._timer = null;
        this._initialPosition = null;
        this._state = stateEnum.ready();
    }

    /**
     * Validate a tap after it ends and call the passed in callback if it passes.
     * @param  {Object}   endData   Data from previous tap
     * @param  {Object}   newData     Data from current tap
     * @param  {Function} callback  Callback to fire on successfuly validation
     */
    _tryNextState(endData, { time, position }, callback) {
        // Criteria for a successful tap:
        // The touch must not have been held for longer than THRESHOLD_HOLD_TIME
        // The touch must not have moved farther than THRESHOLD_DISTANCE
        if (
            endData.time - time > THRESHOLD_HOLD_TIME ||
            getTouchDistance(endData.position, position) > THRESHOLD_DISTANCE
        ) {
            this._resetState();
        } else {
            callback(endData);
        }
    }

    _handleFirstTap(data) {
        // Create a timer that expires if a second tap does not happen before THRESHOLD_TIME
        this._initialPosition = data.position;
        this._timer = setTimeout(() => {
            // If the timer finishes then start over because we didn't get a second tap in time.
            this._resetState();
        }, THRESHOLD_TIME);
    }

    _handleSecondTap(data) {
        const { position } = data;

        // Check if the second tap was within THRESHOLD_DISTANCE of the first tap
        if (getTouchDistance(position, this._initialPosition) > THRESHOLD_DISTANCE) {
            this._resetState();
        } else {
            this._onDoubleTap(position.x, position.y);
        }
    }

    destroy() {
        clearTimeout(this._timer);
        this._multiTouchDetector.destroy();
    }
}

module.exports = DoubleTapDetector;



// WEBPACK FOOTER //
// ./assets/js/global/utils/touch/DoubleTapDetector.es.js